import React, { useState, useEffect, useRef } from 'react';
import { AdminHeader } from './AdminLayout';
import { 
    getLehrpersonen, 
    createLehrperson, 
    updateLehrperson, 
    deleteLehrperson,
    uploadLehrpersonFoto,
    deleteLehrpersonFoto,
    getInstrumente,
    getGemeinden,
    getUnterrichtsorte,
    downloadLehrpersonenTemplate,
    importLehrpersonenCSV,
    swapLehrpersonNames,
    importLehrpersonenFotosZip,
    exportLehrpersonenJSON,
    importLehrpersonenJSON,
    exportLehrpersonenCSV,
    getErrorMessage
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    Users, 
    Search, 
    Mail, 
    Phone, 
    Music, 
    MapPin, 
    Building2,
    Check,
    ChevronDown,
    X,
    ImageOff,
    Upload,
    Camera,
    Download,
    FileSpreadsheet,
    AlertCircle,
    UserCheck,
    CheckCircle2,
    ArrowLeftRight,
    Image,
    Archive,
    FolderSync,
    FileDown,
    FileUp,
    Database
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LehrpersonenList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteItem, setDeleteItem] = useState(null);
    
    // Sheet state
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ 
        vorname: '', 
        nachname: '', 
        email: '', 
        telefon: '',
        instrumente: [],
        gemeinden: [],
        typ: 'lehrperson'
    });
    
    // Options for selections
    const [allInstrumente, setAllInstrumente] = useState([]);
    const [allGemeinden, setAllGemeinden] = useState([]);
    const [allOrte, setAllOrte] = useState([]);
    
    // UI state
    const [showAllInstruments, setShowAllInstruments] = useState(false);
    const [showAllGemeinden, setShowAllGemeinden] = useState(false);
    
    // Photo state
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [currentFotoUrl, setCurrentFotoUrl] = useState(null);
    
    // Unified Import/Export Dialog
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [dataDialogTab, setDataDialogTab] = useState('export');
    
    // CSV Import state
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const csvInputRef = useRef(null);
    
    // Photo ZIP Import state
    const [photoImportFile, setPhotoImportFile] = useState(null);
    const [photoImporting, setPhotoImporting] = useState(false);
    const [photoImportResult, setPhotoImportResult] = useState(null);
    const [photoOverwrite, setPhotoOverwrite] = useState(false);
    const photoInputRef = useRef(null);
    
    // JSON Import state
    const [jsonImportFile, setJsonImportFile] = useState(null);
    const [jsonImporting, setJsonImporting] = useState(false);
    const [jsonImportResult, setJsonImportResult] = useState(null);
    const jsonInputRef = useRef(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [lehrRes, instRes, gemRes, orteRes] = await Promise.all([
                getLehrpersonen(),
                getInstrumente(),
                getGemeinden(),
                getUnterrichtsorte()
            ]);
            setItems(lehrRes.data.sort((a, b) => a.nachname.localeCompare(b.nachname)));
            setAllInstrumente(instRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setAllGemeinden(gemRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setAllOrte(orteRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredItems = items.filter(item => {
        const fullName = `${item.vorname} ${item.nachname}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) ||
            item.email?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const openSheet = (item = null) => {
        if (item) {
            setEditItem(item);
            setFormData({
                vorname: item.vorname,
                nachname: item.nachname,
                email: item.email || '',
                telefon: item.telefon || '',
                instrumente: item.instrumente || [],
                gemeinden: item.gemeinden || [],
                typ: item.typ || 'lehrperson'
            });
            setCurrentFotoUrl(item.foto_url || null);
        } else {
            setEditItem(null);
            setFormData({ 
                vorname: '', 
                nachname: '', 
                email: '', 
                telefon: '',
                instrumente: [],
                gemeinden: [],
                typ: 'lehrperson'
            });
            setCurrentFotoUrl(null);
        }
        setSelectedPhoto(null);
        setPhotoPreview(null);
        setShowAllInstruments(false);
        setShowAllGemeinden(false);
        setSheetOpen(true);
    };

    const closeSheet = () => {
        setSheetOpen(false);
        setEditItem(null);
        setSelectedPhoto(null);
        setPhotoPreview(null);
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error('Nur JPG, PNG oder WebP erlaubt');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Maximale Dateigröße: 5MB');
                return;
            }
            setSelectedPhoto(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handlePhotoDelete = async () => {
        if (!editItem?.id) return;
        
        try {
            await deleteLehrpersonFoto(editItem.id);
            setCurrentFotoUrl(null);
            toast.success('Foto gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const clearPhotoSelection = () => {
        setSelectedPhoto(null);
        setPhotoPreview(null);
    };

    const toggleInstrument = (instId) => {
        setFormData(prev => ({
            ...prev,
            instrumente: prev.instrumente.includes(instId)
                ? prev.instrumente.filter(id => id !== instId)
                : [...prev.instrumente, instId]
        }));
    };

    const toggleGemeinde = (gemId) => {
        setFormData(prev => ({
            ...prev,
            gemeinden: prev.gemeinden.includes(gemId)
                ? prev.gemeinden.filter(id => id !== gemId)
                : [...prev.gemeinden, gemId]
        }));
    };

    const handleSave = async () => {
        try {
            let savedId = editItem?.id;
            
            if (editItem) {
                await updateLehrperson(editItem.id, formData);
                toast.success('Lehrperson aktualisiert');
            } else {
                const res = await createLehrperson(formData);
                savedId = res.data.id;
                toast.success('Lehrperson erstellt');
            }
            
            // Upload photo if selected
            if (selectedPhoto && savedId) {
                setUploadingPhoto(true);
                try {
                    await uploadLehrpersonFoto(savedId, selectedPhoto);
                    toast.success('Foto hochgeladen');
                } catch (error) {
                    toast.error('Foto-Upload fehlgeschlagen');
                } finally {
                    setUploadingPhoto(false);
                }
            }
            
            closeSheet();
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    const handleDelete = async () => {
        try {
            await deleteLehrperson(deleteItem.id);
            toast.success('Lehrperson gelöscht');
            setDeleteItem(null);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
        }
    };

    // Get names for display
    const getInstrumentName = (id) => allInstrumente.find(i => i.id === id)?.name || '';
    const getGemeindeName = (id) => allGemeinden.find(g => g.id === id)?.name || '';

    // Get unterrichtsorte for selected gemeinden
    const getOrteForGemeinden = () => {
        return allOrte.filter(o => formData.gemeinden.includes(o.gemeinde_id));
    };

    // CSV Import functions
    const handleDownloadTemplate = async () => {
        try {
            const response = await downloadLehrpersonenTemplate();
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'lehrpersonen_vorlage.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Vorlage heruntergeladen');
        } catch (error) {
            toast.error('Fehler beim Download');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.csv')) {
                toast.error('Nur CSV-Dateien erlaubt');
                return;
            }
            setImportFile(file);
            setImportResult(null);
        }
    };

    const handleImport = async () => {
        if (!importFile) return;
        
        setImporting(true);
        try {
            const response = await importLehrpersonenCSV(importFile);
            setImportResult(response.data);
            
            if (response.data.success > 0) {
                toast.success(`${response.data.success} Lehrperson(en) importiert`);
                fetchData(); // Refresh list
            }
            if (response.data.errors.length > 0 && response.data.success === 0) {
                toast.error('Import fehlgeschlagen');
            }
        } catch (error) {
            toast.error('Fehler beim Import');
            setImportResult({ success: 0, errors: [{ error: 'Unbekannter Fehler' }], imported: [] });
        } finally {
            setImporting(false);
        }
    };

    // Photo ZIP Import functions
    const handlePhotoFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.zip')) {
                toast.error('Nur ZIP-Dateien erlaubt');
                return;
            }
            setPhotoImportFile(file);
            setPhotoImportResult(null);
        }
    };

    const handlePhotoImport = async () => {
        if (!photoImportFile) return;
        
        setPhotoImporting(true);
        try {
            const response = await importLehrpersonenFotosZip(photoImportFile, photoOverwrite);
            setPhotoImportResult(response.data);
            
            if (response.data.success > 0) {
                toast.success(`${response.data.success} Foto(s) importiert`);
                fetchData(); // Refresh list to show new photos
            }
            if (response.data.errors.length > 0 && response.data.success === 0) {
                toast.error('Import fehlgeschlagen');
            }
        } catch (error) {
            toast.error('Fehler beim Import');
            setPhotoImportResult({ success: 0, skipped: 0, errors: [{ error: 'Unbekannter Fehler' }], imported: [] });
        } finally {
            setPhotoImporting(false);
        }
    };

    // JSON Export/Import functions
    const handleJSONExport = async () => {
        try {
            const response = await exportLehrpersonenJSON();
            const blob = new Blob([response.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lehrpersonen_backup_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('JSON-Backup heruntergeladen');
        } catch (error) {
            toast.error('Fehler beim Export');
        }
    };

    const handleCSVExport = async () => {
        try {
            const response = await exportLehrpersonenCSV();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lehrpersonen_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('CSV-Export heruntergeladen');
        } catch (error) {
            toast.error('Fehler beim CSV-Export');
        }
    };

    const handleJSONImport = async () => {
        if (!jsonImportFile) return;
        
        setJsonImporting(true);
        try {
            const response = await importLehrpersonenJSON(jsonImportFile);
            setJsonImportResult(response.data);
            
            if (response.data.imported > 0 || response.data.updated > 0) {
                toast.success(`${response.data.imported} importiert, ${response.data.updated} aktualisiert`);
                fetchData();
            }
            if (response.data.errors?.length > 0 && response.data.imported === 0 && response.data.updated === 0) {
                toast.error('Import fehlgeschlagen');
            }
        } catch (error) {
            toast.error('Fehler beim Import');
            setJsonImportResult({ imported: 0, updated: 0, errors: [{ error: 'Unbekannter Fehler' }] });
        } finally {
            setJsonImporting(false);
        }
    };

    const closeDataDialog = () => {
        setDataDialogOpen(false);
        setImportFile(null);
        setImportResult(null);
        setPhotoImportFile(null);
        setPhotoImportResult(null);
        setPhotoOverwrite(false);
        setJsonImportFile(null);
        setJsonImportResult(null);
        if (csvInputRef.current) csvInputRef.current.value = '';
        if (photoInputRef.current) photoInputRef.current.value = '';
        if (jsonInputRef.current) jsonInputRef.current.value = '';
    };

    // Swap names function
    const handleSwapNames = async (item, e) => {
        e.stopPropagation(); // Prevent row click
        
        try {
            await swapLehrpersonNames(item.id);
            toast.success(`Namen getauscht: ${item.nachname} ${item.vorname}`);
            fetchData(); // Refresh list
        } catch (error) {
            toast.error('Fehler beim Tauschen');
        }
    };

    return (
        <>
            <AdminHeader title="Lehrpersonen">
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setDataDialogOpen(true)} 
                        className="rounded-xl"
                        data-testid="lehrpersonen-data-btn"
                    >
                        <FolderSync className="h-4 w-4 mr-2" />
                        Import / Export
                    </Button>
                    <Button onClick={() => openSheet()} className="rounded-xl">
                        <Plus className="h-4 w-4 mr-2" />
                        Neue Lehrperson
                    </Button>
                </div>
            </AdminHeader>

            <div className="admin-content">
                {/* Search */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                    <div className="search-wrapper flex-1 max-w-xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                        <Input
                            placeholder="Name oder E-Mail suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <div className="text-base text-muted-foreground">
                        {filteredItems.length} von {items.length} Lehrpersonen
                    </div>
                </div>

                {/* Table */}
                <div className="data-card">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Foto</th>
                                <th>Name</th>
                                <th>Instrumente</th>
                                <th>Gemeinden</th>
                                <th className="w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [...Array(10)].map((_, i) => (
                                    <tr key={i}>
                                        <td><div className="h-10 w-10 bg-muted rounded-full animate-pulse" /></td>
                                        <td><div className="h-5 bg-muted rounded w-40 animate-pulse" /></td>
                                        <td><div className="h-5 bg-muted rounded w-48 animate-pulse" /></td>
                                        <td><div className="h-5 bg-muted rounded w-32 animate-pulse" /></td>
                                        <td></td>
                                    </tr>
                                ))
                            ) : filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="empty-state py-12">
                                            <Users className="empty-state-icon" />
                                            <p className="empty-state-title">Keine Lehrpersonen gefunden</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredItems.map(item => (
                                    <tr 
                                        key={item.id} 
                                        className="cursor-pointer"
                                        onClick={() => openSheet(item)}
                                        data-testid={`lehrperson-row-${item.id}`}
                                    >
                                        <td>
                                            {/* Photo column */}
                                            <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                                                {item.foto_url ? (
                                                    <img 
                                                        src={`${API_URL}${item.foto_url}`}
                                                        alt={`${item.vorname} ${item.nachname}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-slate-400"><svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>`;
                                                        }}
                                                    />
                                                ) : (
                                                    <ImageOff className="w-5 h-5 text-slate-300" />
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <p className="font-semibold">{item.vorname} {item.nachname}</p>
                                                    {item.email && (
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Mail className="h-3 w-3" />
                                                            {item.email}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-wrap gap-1">
                                                {item.instrumente?.slice(0, 3).map(instId => {
                                                    const inst = allInstrumente.find(i => i.id === instId);
                                                    return inst ? (
                                                        <span key={instId} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                                                            {inst.name}
                                                        </span>
                                                    ) : null;
                                                })}
                                                {(item.instrumente?.length || 0) > 3 && (
                                                    <span className="text-xs text-muted-foreground">+{item.instrumente.length - 3}</span>
                                                )}
                                                {(!item.instrumente || item.instrumente.length === 0) && (
                                                    <span className="text-muted-foreground/50 text-sm">—</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-wrap gap-1">
                                                {item.gemeinden?.slice(0, 2).map(gemId => {
                                                    const gem = allGemeinden.find(g => g.id === gemId);
                                                    return gem ? (
                                                        <span key={gemId} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            {gem.name}
                                                        </span>
                                                    ) : null;
                                                })}
                                                {(item.gemeinden?.length || 0) > 2 && (
                                                    <span className="text-xs text-muted-foreground">+{item.gemeinden.length - 2}</span>
                                                )}
                                                {(!item.gemeinden || item.gemeinden.length === 0) && (
                                                    <span className="text-muted-foreground/50 text-sm">—</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => openSheet(item)} className="btn-icon">
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => setDeleteItem(item)} className="btn-icon btn-icon-destructive">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sheet for Edit/Create */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                    <SheetHeader className="mb-6">
                        <SheetTitle className="text-xl">
                            {editItem ? 'Lehrperson bearbeiten' : 'Neue Lehrperson'}
                        </SheetTitle>
                        <SheetDescription>
                            {editItem 
                                ? 'Bearbeite Kontaktdaten und Verknüpfungen'
                                : 'Erstelle eine neue Lehrperson mit allen Verknüpfungen'
                            }
                        </SheetDescription>
                    </SheetHeader>
                    
                    <div className="space-y-6 pb-6">
                        {/* Photo Upload */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <Camera className="w-4 h-4 text-emerald-600" />
                                Foto
                            </h3>
                            
                            <div className="flex items-start gap-4">
                                {/* Photo Preview */}
                                <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex-shrink-0">
                                    {photoPreview ? (
                                        <>
                                            <img src={photoPreview} alt="Vorschau" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={clearPhotoSelection}
                                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </>
                                    ) : currentFotoUrl ? (
                                        <>
                                            <img src={`${API_URL}${currentFotoUrl}`} alt="Aktuelles Foto" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={handlePhotoDelete}
                                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                                title="Foto löschen"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <ImageOff className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                
                                {/* Upload Button */}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        id="photo-upload"
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        onChange={handlePhotoSelect}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="photo-upload"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors text-sm font-medium"
                                    >
                                        <Upload className="w-4 h-4" />
                                        {currentFotoUrl || photoPreview ? 'Ersetzen' : 'Hochladen'}
                                    </label>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        JPG, PNG oder WebP • Max. 5MB
                                    </p>
                                    {photoPreview && (
                                        <p className="text-xs text-emerald-600 mt-1">
                                            ✓ Neues Foto ausgewählt (wird beim Speichern hochgeladen)
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Basic Info */}
                        <div className="space-y-4 pt-4 border-t">
                            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-600" />
                                Kontaktdaten
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="vorname" className="text-sm">Vorname *</Label>
                                    <Input
                                        id="vorname"
                                        value={formData.vorname}
                                        onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                                        placeholder="Max"
                                        className="mt-1"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="nachname" className="text-sm">Nachname *</Label>
                                    <Input
                                        id="nachname"
                                        value={formData.nachname}
                                        onChange={(e) => setFormData({ ...formData, nachname: e.target.value })}
                                        placeholder="Mustermann"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <Label htmlFor="email" className="text-sm">E-Mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="max.mustermann@beispiel.at"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div>
                                <Label htmlFor="telefon" className="text-sm">Telefon</Label>
                                <Input
                                    id="telefon"
                                    type="tel"
                                    value={formData.telefon}
                                    onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                                    placeholder="+43 664 1234567"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        {/* Person Type Toggle Group */}
                        <div className="pt-4 border-t">
                            <div className="flex items-start gap-3">
                                <UserCheck className="w-4 h-4 text-amber-600 mt-0.5" />
                                <div className="flex-1">
                                    <Label className="text-sm font-semibold">
                                        Typ
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                                        Bestimmt, ob Angebote erwartet werden
                                    </p>
                                    <div className="space-y-2">
                                        {/* Lehrperson */}
                                        <div 
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                formData.typ === 'lehrperson' 
                                                    ? 'bg-emerald-50 border-emerald-200' 
                                                    : 'hover:bg-gray-50'
                                            }`}
                                            onClick={() => setFormData({ ...formData, typ: 'lehrperson' })}
                                        >
                                            <div>
                                                <p className="text-sm font-medium">Lehrperson</p>
                                                <p className="text-xs text-muted-foreground">Muss Angebote eingeben</p>
                                            </div>
                                            <Switch
                                                checked={formData.typ === 'lehrperson'}
                                                onCheckedChange={() => setFormData({ ...formData, typ: 'lehrperson' })}
                                            />
                                        </div>
                                        
                                        {/* Administration */}
                                        <div 
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                formData.typ === 'administration' 
                                                    ? 'bg-amber-50 border-amber-200' 
                                                    : 'hover:bg-gray-50'
                                            }`}
                                            onClick={() => setFormData({ ...formData, typ: 'administration' })}
                                        >
                                            <div>
                                                <p className="text-sm font-medium">Administration</p>
                                                <p className="text-xs text-muted-foreground">Kontakt für öffentliche Seite</p>
                                            </div>
                                            <Switch
                                                checked={formData.typ === 'administration'}
                                                onCheckedChange={() => setFormData({ ...formData, typ: 'administration' })}
                                            />
                                        </div>
                                        
                                        {/* Extern */}
                                        <div 
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                formData.typ === 'extern' 
                                                    ? 'bg-blue-50 border-blue-200' 
                                                    : 'hover:bg-gray-50'
                                            }`}
                                            onClick={() => setFormData({ ...formData, typ: 'extern' })}
                                        >
                                            <div>
                                                <p className="text-sm font-medium">Extern</p>
                                                <p className="text-xs text-muted-foreground">Externe Koordinatoren</p>
                                            </div>
                                            <Switch
                                                checked={formData.typ === 'extern'}
                                                onCheckedChange={() => setFormData({ ...formData, typ: 'extern' })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Instrumente */}
                        <div className="pt-4 border-t">
                            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                <Music className="w-4 h-4 text-violet-600" />
                                Instrumente
                                <span className="text-muted-foreground font-normal">({formData.instrumente.length})</span>
                            </h3>
                            
                            {/* Selected instruments */}
                            {formData.instrumente.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.instrumente.map(instId => (
                                        <button
                                            key={instId}
                                            onClick={() => toggleInstrument(instId)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-violet-600 text-white"
                                        >
                                            {getInstrumentName(instId)}
                                            <X className="w-3 h-3" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            {/* Add instruments */}
                            {!showAllInstruments ? (
                                <button
                                    onClick={() => setShowAllInstruments(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-violet-600 hover:bg-violet-50 rounded-full transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Instrument hinzufügen
                                </button>
                            ) : (
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {allInstrumente.filter(i => !formData.instrumente.includes(i.id)).map(inst => (
                                            <button
                                                key={inst.id}
                                                onClick={() => toggleInstrument(inst.id)}
                                                className="px-3 py-1.5 rounded-full text-sm border bg-white border-slate-200 text-slate-600 hover:border-violet-400 hover:text-violet-600 transition-colors"
                                            >
                                                {inst.name}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setShowAllInstruments(false)} 
                                        className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                        <ChevronDown className="w-3 h-3 inline rotate-180" /> Schließen
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* Gemeinden */}
                        <div className="pt-4 border-t">
                            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                <Building2 className="w-4 h-4 text-emerald-600" />
                                Gemeinden
                                <span className="text-muted-foreground font-normal">({formData.gemeinden.length})</span>
                            </h3>
                            
                            {/* Selected gemeinden */}
                            {formData.gemeinden.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.gemeinden.map(gemId => (
                                        <button
                                            key={gemId}
                                            onClick={() => toggleGemeinde(gemId)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-600 text-white"
                                        >
                                            {getGemeindeName(gemId)}
                                            <X className="w-3 h-3" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            {/* Add gemeinden */}
                            {!showAllGemeinden ? (
                                <button
                                    onClick={() => setShowAllGemeinden(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Gemeinde hinzufügen
                                </button>
                            ) : (
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {allGemeinden.filter(g => !formData.gemeinden.includes(g.id)).map(gem => (
                                            <button
                                                key={gem.id}
                                                onClick={() => toggleGemeinde(gem.id)}
                                                className="px-3 py-1.5 rounded-full text-sm border bg-white border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                                            >
                                                {gem.name}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setShowAllGemeinden(false)} 
                                        className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                        <ChevronDown className="w-3 h-3 inline rotate-180" /> Schließen
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* Unterrichtsorte (read-only, derived from gemeinden) */}
                        {formData.gemeinden.length > 0 && (
                            <div className="pt-4 border-t">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                    <MapPin className="w-4 h-4 text-amber-600" />
                                    Unterrichtsorte
                                    <span className="text-muted-foreground font-normal text-xs">(automatisch aus Gemeinden)</span>
                                </h3>
                                
                                <div className="flex flex-wrap gap-2">
                                    {getOrteForGemeinden().map(ort => (
                                        <span
                                            key={ort.id}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200"
                                        >
                                            {ort.name}
                                            <span className="text-xs text-amber-500">({getGemeindeName(ort.gemeinde_id)})</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Save Button */}
                        <div className="pt-6 border-t flex gap-3">
                            <Button 
                                variant="outline" 
                                onClick={closeSheet}
                                className="flex-1 rounded-xl"
                            >
                                Abbrechen
                            </Button>
                            <Button 
                                onClick={handleSave} 
                                disabled={!formData.vorname.trim() || !formData.nachname.trim()}
                                className="flex-1 rounded-xl"
                                data-testid="save-lehrperson-btn"
                            >
                                {editItem ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl">Lehrperson löschen?</AlertDialogTitle>
                        <AlertDialogDescription className="text-base">
                            Möchtest du "{deleteItem?.vorname} {deleteItem?.nachname}" wirklich löschen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* CSV Import Dialog */}
            <Dialog open={dataDialogOpen} onOpenChange={closeDataDialog}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Database className="h-5 w-5 text-primary" />
                            Daten Import / Export
                        </DialogTitle>
                        <DialogDescription>
                            Exportiere Lehrpersonen-Daten als Backup oder importiere aus verschiedenen Quellen.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="export" value={dataDialogTab} onValueChange={setDataDialogTab} className="mt-4">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="export" className="flex items-center gap-2">
                                <FileDown className="h-4 w-4" />
                                Exportieren
                            </TabsTrigger>
                            <TabsTrigger value="import" className="flex items-center gap-2">
                                <FileUp className="h-4 w-4" />
                                Importieren
                            </TabsTrigger>
                        </TabsList>
                        
                        {/* EXPORT TAB */}
                        <TabsContent value="export" className="space-y-4">
                            <div className="grid gap-4">
                                {/* JSON Backup Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <Download className="h-5 w-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">Vollständiges Backup (JSON)</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Alle Daten inkl. IDs für Wiederherstellung
                                                </p>
                                            </div>
                                        </div>
                                        <Button onClick={handleJSONExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* CSV Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">CSV-Export</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Für Excel/Tabellenkalkulation
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="outline" onClick={handleCSVExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                        
                        {/* IMPORT TAB */}
                        <TabsContent value="import" className="space-y-6">
                            {/* JSON Restore */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Upload className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Backup wiederherstellen (JSON)</p>
                                        <p className="text-sm text-muted-foreground">
                                            Aus zuvor exportiertem JSON-Backup. Bestehende Einträge werden aktualisiert.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={(e) => setJsonImportFile(e.target.files[0])}
                                        ref={jsonInputRef}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        onClick={handleJSONImport} 
                                        disabled={!jsonImportFile || jsonImporting}
                                        className="rounded-xl"
                                    >
                                        {jsonImporting ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                {jsonImportResult && (
                                    <div className="mt-3">
                                        {(jsonImportResult.imported > 0 || jsonImportResult.updated > 0) ? (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">Erfolgreich!</span>
                                                <span className="text-sm">{jsonImportResult.imported} neu, {jsonImportResult.updated} aktualisiert</span>
                                            </div>
                                        ) : jsonImportResult.errors?.length > 0 && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                                                <AlertCircle className="h-4 w-4 inline mr-2" />
                                                {jsonImportResult.errors.map(e => e.error).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* CSV Import */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Neue Lehrpersonen aus CSV</p>
                                        <p className="text-sm text-muted-foreground">
                                            Fügt neue Einträge hinzu. <button onClick={handleDownloadTemplate} className="text-primary underline">Vorlage herunterladen</button>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        ref={csvInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileSelect}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        variant="outline"
                                        onClick={handleImport} 
                                        disabled={!importFile || importing}
                                        className="rounded-xl"
                                    >
                                        {importing ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                {importResult && (
                                    <div className="mt-3">
                                        {importResult.success > 0 && (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">{importResult.success} importiert</span>
                                            </div>
                                        )}
                                        {importResult.errors?.length > 0 && (
                                            <div className="p-3 mt-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm max-h-24 overflow-y-auto">
                                                {importResult.errors.map((err, i) => (
                                                    <div key={i}>{err.row && `Zeile ${err.row}: `}{err.error}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* Photo ZIP Import */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-violet-100 rounded-lg">
                                        <Image className="h-5 w-5 text-violet-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Fotos aus ZIP-Datei</p>
                                        <p className="text-sm text-muted-foreground">
                                            Dateiname: <code className="bg-slate-100 px-1 rounded">Nachname_Vorname.jpg</code>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 mb-3">
                                    <input
                                        ref={photoInputRef}
                                        type="file"
                                        accept=".zip"
                                        onChange={handlePhotoFileSelect}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        variant="outline"
                                        onClick={handlePhotoImport} 
                                        disabled={!photoImportFile || photoImporting}
                                        className="rounded-xl"
                                    >
                                        {photoImporting ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Switch 
                                        id="photo-overwrite"
                                        checked={photoOverwrite}
                                        onCheckedChange={setPhotoOverwrite}
                                    />
                                    <Label htmlFor="photo-overwrite" className="text-muted-foreground cursor-pointer">
                                        Vorhandene Fotos ersetzen
                                    </Label>
                                </div>
                                {photoImportResult && (
                                    <div className="mt-3">
                                        {photoImportResult.success > 0 && (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">{photoImportResult.success} Foto(s) importiert</span>
                                            </div>
                                        )}
                                        {photoImportResult.skipped > 0 && (
                                            <div className="p-2 mt-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
                                                {photoImportResult.skipped} übersprungen (bereits vorhanden)
                                            </div>
                                        )}
                                        {photoImportResult.errors?.length > 0 && (
                                            <div className="p-3 mt-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm max-h-24 overflow-y-auto">
                                                {photoImportResult.errors.map((err, i) => (
                                                    <div key={i}>{err.file}: {err.error}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                    
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={closeDataDialog} className="rounded-xl">
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
